import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ChevronRight, Award, RefreshCw, HelpCircle } from 'lucide-react';
import PageShell from './PageShell';
import { RVP_CONTENT, RVP_LIST } from '../data/rendezvousPedagogiques';

/**
 * Page publique de quiz autonome pour élèves + parents.
 * Reprend automatiquement TOUS les QCM d'un RVP donné, pour les refaire à la maison.
 * Accessible via /quiz/rvp/:slug (QR code affiché en fin de RVP).
 */
export default function RvpQuizPage() {
  const { slug } = useParams();
  const rvp = useMemo(() => RVP_LIST.find((r) => r.slug === slug), [slug]);
  const questions = useMemo(() => {
    const slides = RVP_CONTENT[slug] || [];
    return slides.filter((s) => s?.type === 'qcm');
  }, [slug]);

  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const selectAnswer = (qIdx, optIdx) => {
    if (showResults) return;
    setAnswers((a) => ({ ...a, [qIdx]: optIdx }));
  };

  const submit = () => setShowResults(true);
  const reset = () => { setAnswers({}); setShowResults(false); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const correctCount = questions.reduce((acc, q, i) => {
    const opt = q.options[answers[i]];
    return acc + (opt?.correct ? 1 : 0);
  }, 0);
  const total = questions.length;
  const score = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const allAnswered = questions.every((_, i) => answers[i] !== undefined);

  if (!rvp || total === 0) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto p-8 text-center">
          <h1 className="font-serif-editorial text-white text-[28px]">Quiz introuvable</h1>
          <p className="mt-4 text-white/60">Aucun quiz disponible pour ce rendez-vous.</p>
          <Link to="/" className="mt-6 inline-block px-5 h-11 leading-[44px] rounded-full bg-white/10 text-white text-[13px] font-semibold hover:bg-white/20">Retour à l&apos;accueil</Link>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 lg:px-6 py-10 sm:py-14" data-testid="rvp-quiz-page">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Quiz · {rvp.code}</span>
        </nav>

        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
          <HelpCircle size={11} /> Quiz · {rvp.code}
        </div>
        <h1 className="mt-3 font-serif-editorial text-white text-[32px] sm:text-[42px] leading-tight">Refaire le quiz à la maison</h1>
        <p className="mt-3 text-white/70 text-[14.5px] max-w-2xl leading-relaxed">
          Retrouvez ici les {total} questions abordées lors du <strong className="text-white">{rvp.title}</strong>. À faire tranquillement, en famille — parents et élève ensemble, pour ancrer les réponses.
        </p>

        {/* Score en tête (si résultats affichés) */}
        {showResults && (
          <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/25 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Award size={40} className="text-emerald-300 shrink-0" strokeWidth={1.4} />
            <div className="flex-1">
              <div className="text-emerald-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">Résultat</div>
              <div className="mt-1 font-serif-editorial text-white text-[36px] leading-none">{correctCount} / {total} <span className="text-white/50 text-[22px]">· {score} %</span></div>
              <p className="mt-2 text-white/70 text-[13.5px]">{score === 100 ? 'Parfait ! Tu maîtrises tous les fondamentaux 👏' : score >= 70 ? 'Bien joué ! Relis les questions ratées.' : 'Reprends tranquillement les explications ci-dessous, puis refais le quiz.'}</p>
            </div>
            <button onClick={reset} className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold transition">
              <RefreshCw size={13} /> Refaire le quiz
            </button>
          </div>
        )}

        {/* Questions */}
        <div className="mt-8 space-y-5">
          {questions.map((q, qi) => {
            const userAnswer = answers[qi];
            const answered = userAnswer !== undefined;
            return (
              <div key={qi} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`quiz-q-${qi}`}>
                <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Question {qi + 1} / {total}</div>
                <div className="mt-2 font-serif-editorial text-white text-[18px] sm:text-[20px] leading-snug">{q.question}</div>
                <div className="mt-4 space-y-2">
                  {q.options.map((opt, oi) => {
                    const isSelected = userAnswer === oi;
                    const showCorrect = showResults && opt.correct;
                    const showWrong = showResults && isSelected && !opt.correct;
                    const cls = showCorrect
                      ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-100'
                      : showWrong
                      ? 'border-red-500/60 bg-red-950/30 text-red-100'
                      : isSelected
                      ? 'border-white/40 bg-white/10 text-white'
                      : 'border-white/10 bg-white/[0.02] text-white/80 hover:border-white/25 hover:bg-white/[0.05]';
                    const Icon = showCorrect ? CheckCircle2 : showWrong ? XCircle : null;
                    return (
                      <button
                        key={oi}
                        onClick={() => selectAnswer(qi, oi)}
                        disabled={showResults}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${cls} ${showResults ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-mono-spec ${isSelected ? 'border-white/60' : 'border-white/25'}`}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <span className="flex-1 text-[13.5px] leading-snug">{opt.text}</span>
                        {Icon && <Icon size={16} className="shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {showResults && q.explanation && (
                  <div className="mt-4 p-4 rounded-xl border border-amber-500/25 bg-amber-950/15">
                    <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Explication</div>
                    <p className="mt-1 text-white/85 text-[13.5px] leading-relaxed">{q.explanation}</p>
                  </div>
                )}
                {!showResults && answered && (
                  <div className="mt-3 text-white/40 text-[11.5px] font-mono-spec">Réponse enregistrée · A vérifier à la fin</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Submit bar */}
        {!showResults && (
          <div className="mt-8 rounded-2xl border border-red-500/30 bg-red-950/15 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="text-red-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">Prêt à valider ?</div>
              <p className="mt-1 text-white/80 text-[13.5px]">
                {allAnswered ? "Toutes les questions sont répondues 👌" : `Encore ${total - Object.keys(answers).length} question(s) sans réponse.`}
              </p>
            </div>
            <button
              onClick={submit}
              disabled={!allAnswered}
              className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14px] font-bold transition"
              data-testid="quiz-submit"
            >
              Voir mes résultats <ChevronRight size={15} />
            </button>
          </div>
        )}

        {showResults && (
          <div className="mt-8 text-center">
            <Link to="/formation/conduite-accompagnee-aac" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 text-[13px] font-semibold transition">
              Revoir la fiche complète AAC →
            </Link>
          </div>
        )}
      </section>
    </PageShell>
  );
}
